<template>
    <div class="space-y-4">
        <header>
            <h2 class="text-lg font-extrabold tracking-tight">Оформлення</h2>
            <p class="mt-1 text-sm text-slate-600 dark:text-white/65">Налаштуйте вигляд свого профілю.</p>
        </header>

        <section class="acc-glass overflow-hidden rounded-2xl border border-white/10">
            <div class="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-5 py-3">
                <div class="text-sm font-extrabold">Попередній перегляд</div>
                <div
                    class="inline-flex rounded-xl border border-foxy-border bg-foxy-surface/80 p-1"
                    role="group"
                    aria-label="Розмір попереднього перегляду"
                >
                    <button
                        v-for="mode in previewModes"
                        :key="mode.value"
                        type="button"
                        class="rounded-lg px-3 py-1 text-xs font-bold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action"
                        :class="previewMode === mode.value ? 'bg-foxy-elevated text-foxy-text shadow-sm' : 'text-foxy-text-secondary'"
                        :aria-pressed="previewMode === mode.value"
                        @click="previewMode = mode.value"
                    >
                        {{ mode.label }}
                    </button>
                </div>
            </div>
            <div
                class="acc-profile-hero-preview-stage"
                :class="{
                    'acc-profile-hero-preview-stage--desktop': previewMode === 'desktop',
                    'acc-profile-hero-preview-stage--mobile': previewMode === 'mobile',
                }"
            >
                <div
                    ref="focalViewport"
                    class="acc-profile-hero-surface acc-profile-hero-identity-viewport acc-profile-hero-preview-viewport acc-profile-cover-positioner relative overflow-hidden rounded-2xl border border-white/10"
                    :class="{
                        'acc-profile-hero-preview-viewport--desktop': previewMode === 'desktop',
                        'acc-profile-hero-preview-viewport--mobile': previewMode === 'mobile',
                        'is-dragging': focalDrag.active,
                        'is-disabled': !headerPreview,
                    }"
                    :style="previewCssVars"
                    :tabindex="headerPreview && isOwner ? 0 : -1"
                    role="group"
                    aria-label="Позиція обкладинки профілю"
                    @pointerdown="startFocalDrag"
                    @pointermove="moveFocalDrag"
                    @pointerup="endFocalDrag"
                    @pointercancel="endFocalDrag"
                    @lostpointercapture="endFocalDrag"
                    @keydown="onFocalKeydown"
                >
                    <img
                        v-if="headerPreview"
                        :src="headerPreview"
                        class="acc-profile-hero-cover absolute inset-0 h-full w-full"
                        alt="Попередній перегляд обкладинки профілю"
                    />
                    <div v-else class="acc-profile-hero-fallback absolute inset-0" />
                    <div class="acc-profile-hero-scrim absolute inset-0" />
                    <div
                        class="acc-profile-hero-preview-content relative z-10 flex h-full items-center p-4"
                        :class="{
                            'acc-profile-hero-preview-content--desktop': previewMode === 'desktop',
                            'acc-profile-hero-preview-content--mobile': previewMode === 'mobile',
                        }"
                    >
                        <div
                            class="acc-profile-hero-identity-shell"
                            :class="{
                                'acc-profile-hero-identity-shell--desktop': previewMode === 'desktop',
                                'acc-profile-hero-identity-shell--mobile': previewMode === 'mobile',
                            }"
                        >
                            <user-avatar
                                :src="profile && profile.avatar ? profile.avatar : ''"
                                :name="profile && profile.name ? profile.name : ''"
                                alt="Аватар у попередньому перегляді"
                                size="hero"
                                class="acc-profile-hero-avatar shrink-0 bg-foxy-surface"
                            />
                            <div class="acc-profile-hero-identity-copy">
                                <div class="truncate text-xl font-extrabold text-foxy-text sm:text-2xl">{{ displayName }}</div>
                                <div class="truncate text-sm text-foxy-text-secondary">@{{ username }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="border-t border-white/10 px-5 py-4">
                <p class="text-xs text-foxy-text-secondary">Перетягніть обкладинку, щоб вибрати потрібний фрагмент.</p>
                <div class="mt-3 flex flex-wrap items-center gap-3">
                    <button
                        type="button"
                        class="acc-btn-primary"
                        :disabled="!isOwner || !headerPreview || positionSaving || !hasUnsavedPosition"
                        @click="saveCoverPosition"
                    >
                        {{ positionSaving ? 'Збереження...' : 'Зберегти позицію' }}
                    </button>
                    <span v-if="positionError" class="text-xs text-red-700 dark:text-red-300" role="alert">{{ positionError }}</span>
                    <span v-if="positionMessage" class="text-xs text-[#9A4A1B] dark:text-[#F3A36F]">{{ positionMessage }}</span>
                </div>
            </div>
        </section>

        <section class="acc-glass rounded-2xl border border-white/10 p-5">
            <div class="text-sm font-extrabold">Колір профілю</div>
            <p class="mt-1 text-xs text-slate-600 dark:text-white/60">Оберіть один із шести фірмових відтінків.</p>
            <div class="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                <button
                    v-for="option in palette"
                    :key="option.hex"
                    type="button"
                    class="rounded-2xl border bg-foxy-surface/80 p-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action focus-visible:ring-offset-2"
                    :class="form.accent_color === option.hex ? 'shadow-md' : 'border-foxy-border hover:bg-foxy-elevated'"
                    :style="form.accent_color === option.hex ? { borderColor: option.hex, boxShadow: `0 0 0 1px ${option.hex}` } : null"
                    :aria-pressed="form.accent_color === option.hex"
                    :disabled="!isOwner || saving"
                    @click="selectAccent(option.hex)"
                >
                    <span class="flex items-center gap-2"
                        ><span class="h-5 w-5 shrink-0 rounded-full border border-black/10" :style="{ backgroundColor: option.hex }" /><span
                            class="min-w-0 truncate text-xs font-bold"
                            >{{ option.name }}</span
                        ></span
                    >
                </button>
            </div>
            <div v-if="accentError" class="mt-3 text-xs text-red-700 dark:text-red-300" role="alert">{{ accentError }}</div>
            <div v-if="accentMessage" class="mt-3 text-xs text-[#9A4A1B] dark:text-[#F3A36F]">{{ accentMessage }}</div>
            <button type="button" class="acc-btn-primary mt-4 w-full justify-center" :disabled="!isOwner || saving" @click="saveAccent">
                {{ saving ? 'Збереження...' : 'Зберегти колір' }}
            </button>
        </section>

        <section class="acc-glass rounded-2xl border border-white/10 p-5">
            <div class="text-sm font-extrabold">Обкладинка профілю</div>
            <div class="mt-4 aspect-[3/1] overflow-hidden rounded-2xl border border-foxy-border bg-foxy-elevated" :style="coverPositionVars">
                <img v-if="headerPreview" :src="headerPreview" class="acc-profile-hero-cover h-full w-full" alt="Обкладинка профілю" />
                <div v-else class="flex h-full items-center justify-center text-xs text-foxy-text-secondary">Обкладинку ще не додано</div>
            </div>
            <div class="mt-3 flex flex-wrap items-center gap-2">
                <label class="acc-btn" :class="{ 'pointer-events-none opacity-60': !isOwner || coverBusy }">
                    {{ headerUploading ? 'Завантаження...' : 'Змінити зображення' }}
                    <input
                        type="file"
                        class="hidden"
                        accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                        :disabled="!isOwner || coverBusy"
                        @change="onHeaderSelected"
                    />
                </label>
                <button
                    type="button"
                    class="acc-btn !border-red-500/40 !text-red-700 dark:!text-red-300"
                    :disabled="!isOwner || coverBusy || !headerPreview"
                    @click="removeHeader"
                >
                    {{ removingHeader ? 'Видалення...' : 'Видалити' }}
                </button>
            </div>
            <div class="mt-3 space-y-1 text-xs text-foxy-text-secondary">
                <div>Рекомендовані розміри: 1500 × 500 px</div>
                <div>JPG, JPEG, PNG, WebP · максимум 4 МБ</div>
            </div>
            <div v-if="coverError" class="mt-3 text-xs text-red-700 dark:text-red-300" role="alert">{{ coverError }}</div>
            <div v-if="coverMessage" class="mt-3 text-xs text-[#9A4A1B] dark:text-[#F3A36F]">{{ coverMessage }}</div>
        </section>

        <div v-if="cropModalOpen" class="fixed inset-0 z-[120]">
            <div class="absolute inset-0 bg-black/70 backdrop-blur-sm" @click="closeCropModal" />
            <div class="absolute inset-0 flex items-center justify-center p-4">
                <div class="acc-modal max-w-3xl">
                    <div class="flex items-center justify-between border-b border-foxy-border pb-4">
                        <div>
                            <div class="text-lg font-extrabold">Обрізати обкладинку</div>
                            <div class="mt-1 text-xs text-foxy-text-secondary">Перетягніть зображення та налаштуйте масштаб.</div>
                        </div>
                        <button class="acc-btn !px-3 !py-1.5" type="button" aria-label="Закрити" @click="closeCropModal">✕</button>
                    </div>
                    <div class="mt-4 space-y-4">
                        <div
                            ref="cropViewport"
                            class="relative aspect-[3/1] w-full cursor-grab overflow-hidden rounded-xl border border-foxy-border bg-black/40 active:cursor-grabbing"
                            @mousedown="startDrag"
                            @mousemove="onDrag"
                            @mouseup="endDrag"
                            @mouseleave="endDrag"
                        >
                            <img
                                v-if="cropSource"
                                ref="cropImage"
                                :src="cropSource"
                                alt="Зображення для обрізання"
                                class="pointer-events-none absolute max-w-none select-none"
                                :style="cropImageStyle"
                                draggable="false"
                            />
                        </div>
                        <label class="block"
                            ><span class="mb-2 block text-xs text-foxy-text-secondary">Масштаб</span
                            ><input
                                v-model.number="cropScale"
                                type="range"
                                :min="cropMinScale"
                                :max="Math.max(cropMinScale * 3, cropMinScale + 0.1)"
                                step="0.01"
                                class="w-full"
                                @input="onScaleChange"
                        /></label>
                        <div v-if="coverError" class="text-xs text-red-700 dark:text-red-300" role="alert">{{ coverError }}</div>
                        <div class="flex justify-end gap-2">
                            <button class="acc-btn" type="button" :disabled="headerUploading" @click="closeCropModal">Скасувати</button
                            ><button class="acc-btn-primary" type="button" :disabled="headerUploading" @click="applyCropAndUpload">
                                {{ headerUploading ? 'Завантаження...' : 'Зберегти обкладинку' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="js">
import axios from 'axios';
import { coverPositionCssVars, normalizeCoverPosition } from '../utils/profileAppearance';

const DEFAULT_ACCENT = '#A84F1D';
const MAX_COVER_BYTES = 4 * 1024 * 1024;
const ACCEPTED_COVER_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const PREVIEW_MODES = [
    { value: 'desktop', label: "Комп'ютер" },
    { value: 'mobile', label: 'Телефон' },
];
const FALLBACK_PALETTE = [
    {
        name: 'Лисичковий',
        hex: DEFAULT_ACCENT,
        secondary: '#C7773A',
        soft: '#F3DDD2',
        glow: '#D88A55',
        foreground: { light: '#873B14', dark: '#F3A36F' },
    },
];

export default {
    name: 'SettingsDesign',
    props: { profile: { type: Object, default: null }, isOwner: { type: Boolean, default: false } },
    data() {
        return {
            form: { accent_color: DEFAULT_ACCENT },
            currentHeaderImage: '',
            previewMode: 'desktop',
            coverPosition: normalizeCoverPosition(),
            persistedCoverPosition: normalizeCoverPosition(),
            positionSaving: false,
            focalDrag: { active: false, pointerId: null, startX: 0, startY: 0, originX: 50, originY: 50 },
            saving: false,
            headerUploading: false,
            removingHeader: false,
            cropModalOpen: false,
            cropSource: '',
            cropImageNatural: { width: 0, height: 0 },
            cropViewportSize: { width: 0, height: 0 },
            cropScale: 1,
            cropMinScale: 1,
            cropX: 0,
            cropY: 0,
            dragState: { active: false, startX: 0, startY: 0, originX: 0, originY: 0 },
            accentMessage: '',
            accentError: '',
            coverMessage: '',
            coverError: '',
            positionMessage: '',
            positionError: '',
        };
    },
    computed: {
        previewModes() {
            return PREVIEW_MODES;
        },
        palette() {
            const options = this.profile?.theme?.palette;
            return Array.isArray(options) && options.length === 6 ? options : FALLBACK_PALETTE;
        },
        headerPreview() {
            return this.currentHeaderImage;
        },
        displayName() {
            return this.profile?.name || this.profile?.username || 'Користувач';
        },
        username() {
            return this.profile?.username || 'username';
        },
        selectedPaletteOption() {
            return this.palette.find((option) => option.hex === this.form.accent_color) || FALLBACK_PALETTE[0];
        },
        previewCssVars() {
            const option = this.selectedPaletteOption;
            return {
                '--acc-accent': option.hex,
                '--acc-secondary': option.secondary,
                '--acc-accent-soft': option.soft,
                '--acc-accent-glow': option.glow,
                '--acc-accent-foreground-light': option.foreground.light,
                '--acc-accent-foreground-dark': option.foreground.dark,
                ...this.coverPositionVars,
            };
        },
        coverPositionVars() {
            return coverPositionCssVars(this.coverPosition);
        },
        hasUnsavedPosition() {
            return (
                this.coverPosition.position_x !== this.persistedCoverPosition.position_x ||
                this.coverPosition.position_y !== this.persistedCoverPosition.position_y
            );
        },
        coverBusy() {
            return this.headerUploading || this.removingHeader || this.positionSaving;
        },
        cropImageStyle() {
            return {
                width: `${this.cropImageNatural.width}px`,
                height: `${this.cropImageNatural.height}px`,
                transform: `translate(${this.cropX}px, ${this.cropY}px) scale(${this.cropScale})`,
                transformOrigin: 'top left',
            };
        },
    },
    watch: {
        profile: {
            immediate: true,
            handler(value) {
                if (!value) return;
                this.currentHeaderImage = value?.header?.image || '';
                this.form.accent_color = value?.theme?.accent || DEFAULT_ACCENT;
                this.syncCoverPosition(value?.header?.position);
            },
        },
    },
    methods: {
        selectAccent(accent) {
            this.form.accent_color = accent;
            this.accentError = '';
            this.accentMessage = '';
        },
        extractFieldError(error, field, fallback) {
            return error?.response?.data?.errors?.[field]?.[0] || error?.response?.data?.message || fallback;
        },
        syncCoverPosition(position) {
            const normalized = normalizeCoverPosition(position);
            this.coverPosition = { ...normalized };
            this.persistedCoverPosition = { ...normalized };
            this.focalDrag.active = false;
        },
        clampPosition(value) {
            return Math.min(100, Math.max(0, Math.round(value * 100) / 100));
        },
        startFocalDrag(event) {
            if (!this.isOwner || !this.headerPreview || this.positionSaving || event.button > 0) return;
            event.preventDefault();
            event.currentTarget.setPointerCapture?.(event.pointerId);
            this.focalDrag = {
                active: true,
                pointerId: event.pointerId,
                startX: event.clientX,
                startY: event.clientY,
                originX: this.coverPosition.position_x,
                originY: this.coverPosition.position_y,
            };
            this.positionError = '';
            this.positionMessage = '';
        },
        moveFocalDrag(event) {
            if (!this.focalDrag.active || event.pointerId !== this.focalDrag.pointerId) return;
            event.preventDefault();
            const viewport = this.$refs.focalViewport;
            if (!viewport?.clientWidth || !viewport?.clientHeight) return;
            this.coverPosition = {
                position_x: this.clampPosition(this.focalDrag.originX - ((event.clientX - this.focalDrag.startX) / viewport.clientWidth) * 100),
                position_y: this.clampPosition(this.focalDrag.originY - ((event.clientY - this.focalDrag.startY) / viewport.clientHeight) * 100),
            };
        },
        endFocalDrag(event) {
            if (!this.focalDrag.active || (event?.pointerId !== undefined && event.pointerId !== this.focalDrag.pointerId)) return;
            this.focalDrag.active = false;
        },
        onFocalKeydown(event) {
            if (!this.isOwner || !this.headerPreview || this.positionSaving) return;
            const changes = {
                ArrowLeft: [-1, 0],
                ArrowRight: [1, 0],
                ArrowUp: [0, -1],
                ArrowDown: [0, 1],
            };
            const change = changes[event.key];
            if (!change) return;
            event.preventDefault();
            const step = event.shiftKey ? 10 : 2;
            this.coverPosition = {
                position_x: this.clampPosition(this.coverPosition.position_x + change[0] * step),
                position_y: this.clampPosition(this.coverPosition.position_y + change[1] * step),
            };
            this.positionError = '';
            this.positionMessage = '';
        },
        async saveCoverPosition() {
            if (!this.isOwner || !this.headerPreview || this.positionSaving || !this.hasUnsavedPosition) return;
            this.positionSaving = true;
            this.positionError = '';
            this.positionMessage = '';
            try {
                const response = await axios.put('/api/account/profile/header-image/position', this.coverPosition);
                this.syncCoverPosition(response?.data?.profile?.header?.position);
                this.positionMessage = response.data.message || 'Позицію обкладинки збережено.';
                this.$emit('profile-updated', response.data.profile);
            } catch (error) {
                this.positionError = this.extractFieldError(error, 'position_x', 'Не вдалося зберегти позицію обкладинки. Спробуйте ще раз.');
            } finally {
                this.positionSaving = false;
            }
        },
        async saveAccent() {
            if (!this.isOwner) return;
            this.saving = true;
            this.accentError = '';
            this.accentMessage = '';
            try {
                const response = await axios.put('/api/account/profile/design', this.form);
                this.accentMessage = response.data.message || 'Колір профілю оновлено.';
                this.$emit('profile-updated', response.data.profile);
            } catch (error) {
                this.accentError = this.extractFieldError(error, 'accent_color', 'Не вдалося зберегти колір профілю.');
            } finally {
                this.saving = false;
            }
        },
        async onHeaderSelected(event) {
            const file = event?.target?.files?.[0];
            if (event?.target) event.target.value = '';
            if (!file || !this.isOwner) return;
            this.coverError = '';
            this.coverMessage = '';
            if (!ACCEPTED_COVER_TYPES.includes(file.type)) {
                this.coverError = 'Оберіть зображення у форматі JPG, PNG або WebP.';
                return;
            }
            if (file.size > MAX_COVER_BYTES) {
                this.coverError = 'Обкладинка не може перевищувати 4 МБ.';
                return;
            }
            await this.openCropModal(file);
        },
        async openCropModal(file) {
            const objectUrl = URL.createObjectURL(file);
            this.cropSource = objectUrl;
            try {
                const image = await this.loadImage(objectUrl);
                this.cropImageNatural = { width: image.naturalWidth, height: image.naturalHeight };
                this.cropModalOpen = true;
                await this.$nextTick();
                this.initCropState();
            } catch (error) {
                this.coverError = 'Файл пошкоджений або його не вдалося декодувати як зображення.';
                this.closeCropModal();
            }
        },
        closeCropModal() {
            if (this.cropSource) URL.revokeObjectURL(this.cropSource);
            this.cropModalOpen = false;
            this.cropSource = '';
            this.dragState.active = false;
        },
        loadImage(src) {
            return new Promise((resolve, reject) => {
                const image = new Image();
                image.onload = () => resolve(image);
                image.onerror = reject;
                image.src = src;
            });
        },
        initCropState() {
            const viewport = this.$refs.cropViewport;
            if (!viewport) return;
            const vw = viewport.clientWidth;
            const vh = viewport.clientHeight;
            this.cropViewportSize = { width: vw, height: vh };
            const iw = this.cropImageNatural.width;
            const ih = this.cropImageNatural.height;
            if (!iw || !ih) return;
            const minScale = Math.max(vw / iw, vh / ih);
            this.cropMinScale = minScale;
            this.cropScale = minScale;
            this.cropX = (vw - iw * minScale) / 2;
            this.cropY = (vh - ih * minScale) / 2;
            this.clampCropPosition();
        },
        clampCropPosition() {
            const minX = Math.min(0, this.cropViewportSize.width - this.cropImageNatural.width * this.cropScale);
            const minY = Math.min(0, this.cropViewportSize.height - this.cropImageNatural.height * this.cropScale);
            this.cropX = Math.min(0, Math.max(minX, this.cropX));
            this.cropY = Math.min(0, Math.max(minY, this.cropY));
        },
        onScaleChange() {
            this.clampCropPosition();
        },
        startDrag(event) {
            if (!this.cropModalOpen) return;
            this.dragState = { active: true, startX: event.clientX, startY: event.clientY, originX: this.cropX, originY: this.cropY };
        },
        onDrag(event) {
            if (!this.dragState.active) return;
            this.cropX = this.dragState.originX + (event.clientX - this.dragState.startX);
            this.cropY = this.dragState.originY + (event.clientY - this.dragState.startY);
            this.clampCropPosition();
        },
        endDrag() {
            this.dragState.active = false;
        },
        async applyCropAndUpload() {
            if (!this.cropSource || !this.isOwner) return;
            this.headerUploading = true;
            this.coverError = '';
            this.coverMessage = '';
            try {
                const blob = await this.renderCroppedBlob();
                const data = new FormData();
                data.append('header_image', blob, 'profile-header.jpg');
                const response = await axios.post('/api/account/profile/header-image', data, { headers: { 'Content-Type': 'multipart/form-data' } });
                this.currentHeaderImage = response?.data?.profile?.header?.image || '';
                this.syncCoverPosition(response?.data?.profile?.header?.position);
                this.coverMessage = response.data.message || 'Обкладинку профілю оновлено.';
                this.$emit('profile-updated', response.data.profile);
                this.closeCropModal();
            } catch (error) {
                this.coverError = this.extractFieldError(error, 'header_image', 'Не вдалося обрізати або завантажити обкладинку.');
            } finally {
                this.headerUploading = false;
            }
        },
        async removeHeader() {
            if (!this.isOwner || this.removingHeader) return;
            this.removingHeader = true;
            this.coverError = '';
            this.coverMessage = '';
            try {
                const response = await axios.delete('/api/account/profile/header-image');
                this.currentHeaderImage = response?.data?.profile?.header?.image || '';
                this.syncCoverPosition(response?.data?.profile?.header?.position);
                this.coverMessage = response.data.message || 'Обкладинку профілю видалено.';
                this.$emit('profile-updated', response.data.profile);
            } catch (error) {
                this.coverError = this.extractFieldError(error, 'header_image', 'Не вдалося видалити обкладинку профілю.');
            } finally {
                this.removingHeader = false;
            }
        },
        renderCroppedBlob() {
            return new Promise((resolve, reject) => {
                const vw = this.cropViewportSize.width;
                const vh = this.cropViewportSize.height;
                const image = this.$refs.cropImage;
                if (!vw || !vh || !image) {
                    reject(new Error('Crop viewport is unavailable'));
                    return;
                }
                const canvas = document.createElement('canvas');
                canvas.width = 1500;
                canvas.height = 500;
                const context = canvas.getContext('2d');
                if (!context) {
                    reject(new Error('Canvas is unavailable'));
                    return;
                }
                context.drawImage(
                    image,
                    -this.cropX / this.cropScale,
                    -this.cropY / this.cropScale,
                    vw / this.cropScale,
                    vh / this.cropScale,
                    0,
                    0,
                    canvas.width,
                    canvas.height,
                );
                canvas.toBlob(
                    (blob) => {
                        if (blob) resolve(blob);
                        else reject(new Error('Crop failed'));
                    },
                    'image/jpeg',
                    0.9,
                );
            });
        },
    },
};
</script>
